import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Chip,
    CircularProgress,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import { ExpandMoreOutlined } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Header from "../../components/Header";
import { tokens } from "../../theme";
import { listAdminConfigs, setAdminConfigs } from "../../store/actions";
import ConfigField from "./ConfigField";

/**
 * Admin settings: every value an admin can change without a deploy.
 *
 * Named for the screen, not its contents - today the registry holds only Moksha brand
 * fields, but a second category needs no change here.
 *
 * The form is rendered from the backend's config registry — `input` decides the control,
 * `help` the text under it — so a new setting is a backend registry entry and nothing here.
 */

// Mirrors the registry's validators in constants/admin-config-registry.ts, so an error
// appears as the admin types. The backend re-checks everything and is authoritative.
//
// The list rules are not hardcoded per field: `item_label` and `item_pattern` come from the
// registry with the rest of the metadata, so this validates emails and phone numbers with
// the same code and would validate a third list without being touched.
const itemRegex = (config) => {
    if (!config.item_pattern) return null;
    try {
        return new RegExp(config.item_pattern);
    } catch {
        return null; // a pattern this browser cannot compile: leave it to the backend
    }
};

const validateValue = (config, value) => {
    const v = (value ?? "").trim();

    if (config.input === "url") {
        if (!v) return "Enter a website address";
        if (!/^https?:\/\//i.test(v)) return "Start the address with https://";
        try {
            if (!new URL(v).hostname.includes(".")) return "Enter a full address, like https://mokshastays.com";
        } catch {
            return "Enter a full address, like https://mokshastays.com";
        }
        return null;
    }

    if (config.input === "string_list") {
        const noun = config.item_label || "entry";
        let parsed;
        try {
            parsed = JSON.parse(value || "[]");
        } catch {
            return `${config.label} could not be read`;
        }
        if (!Array.isArray(parsed)) return `${config.label} could not be read`;
        if (!parsed.length) return `Add at least one ${noun}`;

        const pattern = itemRegex(config);
        const bad = parsed.find(
            (entry) => typeof entry !== "string" || !entry.trim() || (pattern && !pattern.test(entry.trim()))
        );
        if (bad !== undefined) return `"${bad}" does not look like a ${noun}`;

        const lowered = parsed.map((entry) => String(entry).trim().toLowerCase());
        if (new Set(lowered).size !== lowered.length) return `That ${noun} is already in the list`;
        return null;
    }

    if (config.required && !v) return `Enter ${config.label.toLowerCase()}`;

    // Shape comes from the registry, like item_pattern does for lists, so the rule is not
    // written out a second time here. Blank is handled above - it is valid unless required.
    if (v && config.pattern) {
        try {
            if (!new RegExp(`^${config.pattern}$`).test(config.uppercase ? v.toUpperCase() : v)) {
                return `That does not look like a ${config.label.toLowerCase()}`;
            }
        } catch {
            return null;
        }
    }
    return null;
};

const displayWebsite = (url) => {
    try {
        const parsed = new URL(url);
        return (parsed.host + parsed.pathname).replace(/\/+$/, "");
    } catch {
        return "";
    }
};

const AdminSettings = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const colors = tokens(theme.palette.mode);

    const [configs, setConfigs] = useState([]);
    const [values, setValues] = useState({});
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [loaded, setLoaded] = useState(false);
    // Open categories, by name. Tracking what is open rather than what is shut means the
    // default is closed with no initialisation, and the state survives the refetch after a
    // save — so the section you were working in does not snap shut under you.
    const [expanded, setExpanded] = useState({});

    const { isLoading } = useSelector(({ loading }) => ({ isLoading: loading }));

    const saved = useMemo(() => {
        const map = {};
        configs.forEach((config) => {
            map[config.key] = config.value ?? "";
        });
        return map;
    }, [configs]);

    const fetchConfigs = useCallback(async () => {
        // No category filter: the screen renders whatever the registry defines.
        const data = await dispatch(listAdminConfigs());
        if (data) {
            setConfigs(data);
            const next = {};
            data.forEach((config) => {
                next[config.key] = config.value ?? "";
            });
            setValues(next);
            setErrors({});
        }
        setLoaded(true);
    }, [dispatch]);

    useEffect(() => {
        fetchConfigs();
    }, [fetchConfigs]);

    const dirty = useMemo(
        () => configs.some((config) => (values[config.key] ?? "") !== (saved[config.key] ?? "")),
        [configs, values, saved]
    );

    // Covers a reload or a closed tab. In-app navigation is not intercepted — this router
    // is not a data router, so useBlocker is unavailable here.
    useEffect(() => {
        if (!dirty) return undefined;
        const warn = (event) => {
            event.preventDefault();
            event.returnValue = "";
        };
        window.addEventListener("beforeunload", warn);
        return () => window.removeEventListener("beforeunload", warn);
    }, [dirty]);

    const valueOf = (key) => values[key] ?? "";

    const change = (key, value) => {
        setValues((prev) => ({ ...prev, [key]: value }));
        setErrors((prev) => ({ ...prev, [key]: null }));
    };

    const handleSave = async () => {
        const nextErrors = {};
        configs.forEach((config) => {
            const error = validateValue(config, valueOf(config.key));
            if (error) nextErrors[config.key] = error;
        });

        if (Object.keys(nextErrors).length) {
            setErrors(nextErrors);
            // Open any category holding an error. Otherwise "fix the highlighted fields"
            // points at fields the admin cannot see.
            const withErrors = configs
                .filter((c) => nextErrors[c.key])
                // Same key the accordions are built with below, or the expand never matches.
                .map((c) => c.category_label || c.category || "Settings");
            setExpanded((prev) => {
                const next = { ...prev };
                withErrors.forEach((name) => {
                    next[name] = true;
                });
                return next;
            });
            toast.error("Fix the highlighted fields first");
            return;
        }

        setSaving(true);

        // Upper-cased on the way out where the registry says the value is stored that way,
        // so what the admin sees after saving is what was actually stored.
        const changed = configs
            .filter((config) => (values[config.key] ?? "") !== (saved[config.key] ?? ""))
            .map((config) => {
                const value = values[config.key] ?? "";
                return { key: config.key, value: config.uppercase ? value.toUpperCase() : value };
            });

        if (changed.length) {
            const ok = await dispatch(setAdminConfigs(changed));
            if (ok) {
                toast.success("Changes saved");
                await fetchConfigs();
            }
        }

        setSaving(false);
    };

    // Category, then group inside it, in the order the backend sorted them.
    const categories = useMemo(() => {
        const out = [];
        configs.forEach((config) => {
            const name = config.category_label || config.category || "Settings";
            let category = out.find((c) => c.name === name);
            if (!category) out.push((category = { name, help: config.category_help || "", groups: [] }));

            const groupName = config.group || "";
            let group = category.groups.find((g) => g.name === groupName);
            if (!group) category.groups.push((group = { name: groupName, help: config.group_help || "", items: [] }));
            group.items.push(config);
        });
        return out;
    }, [configs]);

    const provenance = useMemo(() => {
        const touched = configs
            .filter((config) => config.updatedAt && !config.is_default)
            .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))[0];
        if (!touched) return "Not changed yet — these are the built-in defaults.";
        const when = new Date(touched.updatedAt).toLocaleDateString(undefined, {
            day: "numeric",
            month: "short",
            year: "numeric",
        });
        return touched.updated_by ? `Last changed by ${touched.updated_by} · ${when}` : `Last changed ${when}`;
    }, [configs]);

    if (!loaded && isLoading) {
        return (
            <Box m="20px" display="flex" justifyContent="center" mt={8}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box m="20px" mb={15}>
            <Box
                m="20px 0"
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
                flexWrap="wrap"
                gap={2}
            >
                <Box>
                    {/* Names the screen, never a category — the bands below name those. That
                        is what stops the nav label and the header drifting apart again. */}
                    <Header title="SETTINGS" subtitle="Values admins can change without a deploy" />
                    <Typography variant="body2" color={colors.grey[300]} fontSize={12.5} mt={-1.5}>
                        {provenance}
                    </Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={2}>
                    {dirty && (
                        <Typography color={colors.grey[300]} fontSize={13}>
                            Unsaved changes
                        </Typography>
                    )}
                    <Button
                        onClick={handleSave}
                        disabled={!dirty || saving}
                        variant="contained"
                        color="secondary"
                        sx={{ textTransform: "none", minWidth: 130 }}
                    >
                        {saving ? <CircularProgress color="inherit" size={18} /> : "Save changes"}
                    </Button>
                </Box>
            </Box>

            {/* Cards span the page like every other screen here; the controls inside them
                are what stay narrow, so a text field never runs the full width of a monitor. */}
            <Stack spacing={2}>
                {categories.map((category) => {
                    const errorCount = category.groups.reduce(
                        (n, g) => n + g.items.filter((c) => errors[c.key]).length,
                        0
                    );
                    return (
                    <Accordion
                        key={category.name}
                        expanded={Boolean(expanded[category.name])}
                        onChange={() =>
                            setExpanded((prev) => ({ ...prev, [category.name]: !prev[category.name] }))
                        }
                        disableGutters
                        sx={{
                            backgroundColor: colors.primary[400],
                            backgroundImage: "none",
                            boxShadow: "none",
                            borderRadius: 1,
                            "&:before": { display: "none" },
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreOutlined sx={{ color: colors.grey[300] }} />}
                            sx={{ px: 3, py: 1.5 }}
                        >
                            <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                                <Box>
                                    <Typography fontWeight="bold" color={colors.grey[100]} fontSize={17}>
                                        {category.name}
                                    </Typography>
                                    {category.help && (
                                        <Typography color={colors.grey[300]} fontSize={13} mt={0.25} maxWidth="56ch">
                                            {category.help}
                                        </Typography>
                                    )}
                                </Box>
                                {errorCount > 0 && (
                                    <Chip
                                        size="small"
                                        label={`${errorCount} to fix`}
                                        sx={{
                                            backgroundColor: colors.redAccent[800],
                                            color: colors.redAccent[300],
                                            fontSize: 11,
                                        }}
                                    />
                                )}
                            </Box>
                        </AccordionSummary>

                        <AccordionDetails sx={{ px: 3, pt: 0, pb: 3 }}>
                        <Stack spacing={2.5}>
                            {category.groups.map((group) => (
                                <Box
                                    key={group.name}
                                    // Sunken against the accordion's own surface, so the
                                    // groups read as contents rather than as more panels.
                                    sx={{
                                        backgroundColor: colors.primary[500],
                                        borderRadius: 1,
                                        borderLeft: `3px solid ${colors.primary[300]}`,
                                        p: 3,
                                    }}
                                >
                                    {group.name && (
                                        <Typography
                                            sx={{
                                                fontSize: 11,
                                                fontWeight: 600,
                                                letterSpacing: "1.4px",
                                                textTransform: "uppercase",
                                                color: colors.grey[300],
                                            }}
                                        >
                                            {group.name}
                                        </Typography>
                                    )}
                                    {group.help && (
                                        <Typography
                                            color={colors.grey[300]}
                                            fontSize={13}
                                            mt={0.75}
                                            mb={0.5}
                                            maxWidth="52ch"
                                        >
                                            {group.help}
                                        </Typography>
                                    )}

                                    <Stack spacing={3} mt={group.name || group.help ? 2.5 : 0}>
                                        {group.items.map((config) => (
                                            <ConfigField
                                                key={config.key}
                                                config={config}
                                                value={valueOf(config.key)}
                                                error={errors[config.key]}
                                                onChange={(value) => change(config.key, value)}
                                                footnote={
                                                    config.input === "url" && displayWebsite(valueOf(config.key))
                                                        ? `Shown as ${displayWebsite(valueOf(config.key))}`
                                                        : undefined
                                                }
                                            />
                                        ))}
                                    </Stack>
                                </Box>
                            ))}
                        </Stack>
                        </AccordionDetails>
                    </Accordion>
                    );
                })}
            </Stack>
        </Box>
    );
};

export default AdminSettings;
